import { useCallback, useEffect } from 'react';

import { eventService, XApiEventKeys } from 'shared/api/event-service';

import { LITERACY_INSIGHT_DATA_TYPE } from '../constants';

interface Params {
  insightId: string;
  isReady?: boolean;
  dependency: unknown;
}

/**
 * Attaches 'click' and 'contextmenu' event handlers to the links
 * within literacy insight modal content to send xApi events on left and right click.
 * */
export const useLiteracyInsightLinksXApiEvents = ({
  insightId,
  isReady = true,
  dependency,
}: Params) => {
  const handleXApiEvent = useCallback(
    (e: MouseEvent) => {
      const eventTarget = e.target as HTMLAnchorElement;

      const linkHref = eventTarget.href;
      const isRightClick = e.type === 'contextmenu';

      eventService.send(
        XApiEventKeys.selectedLiteracyInsightsModalLink,
        {
          result: {
            destinationUrl: linkHref,
          },
        },
        {
          result: {
            recommendationId: insightId,
            isDynamic: true,
            isRightClick,
          },
        },
      );
    },
    [insightId],
  );

  useEffect(() => {
    if (!isReady) return;

    const selector = `[data-type="${LITERACY_INSIGHT_DATA_TYPE}"]`;

    const literacyInsightLinks =
      document.querySelectorAll<HTMLAnchorElement>(selector);

    literacyInsightLinks.forEach((link) => {
      link.addEventListener('click', handleXApiEvent);
      link.addEventListener('contextmenu', handleXApiEvent);
    });

    return () => {
      literacyInsightLinks.forEach((link) => {
        link.removeEventListener('click', handleXApiEvent);
        link.removeEventListener('contextmenu', handleXApiEvent);
      });
    };
  }, [isReady, handleXApiEvent, dependency]);
};
